import {
  ArrowDown,
  ArrowUp,
  BarChart3,
  DollarSign,
  Eye,
  Hash,
  Percent,
  Search,
  TrendingUp,
} from 'lucide-react'
import { memo, useMemo, useState } from 'react'

interface Props {
  data: Record<string, any>[]
  maxRows?: number
  title?: string
  priorityColumns?: string[]
  colorCodedColumns?: Record<
    string,
    'difficulty' | 'volume' | 'currency' | 'percentage' | 'number' | 'text'
  >
}

// Enhanced column type detection and formatting
const getColumnType = (
  key: string,
  value: any,
  colorCodedColumns?: Record<string, string>
): {
  type: 'difficulty' | 'volume' | 'currency' | 'percentage' | 'number' | 'text'
  icon?: JSX.Element
} => {
  if (colorCodedColumns?.[key]) {
    const type = colorCodedColumns[key] as
      | 'difficulty'
      | 'volume'
      | 'currency'
      | 'percentage'
      | 'number'
      | 'text'
    return { type, icon: getTypeIcon(type) }
  }

  const keyLower = key.toLowerCase()
  const valueStr = String(value).toLowerCase()

  if (keyLower.includes('difficulty') || keyLower.includes('kd')) {
    return { type: 'difficulty', icon: <BarChart3 className="h-3 w-3" /> }
  }
  if (keyLower.includes('volume') || keyLower.includes('search')) {
    return { type: 'volume', icon: <Search className="h-3 w-3" /> }
  }
  if (keyLower.includes('cpc') || keyLower.includes('cost') || keyLower.includes('price')) {
    return { type: 'currency', icon: <DollarSign className="h-3 w-3" /> }
  }
  if (valueStr.includes('%') || keyLower.includes('percent')) {
    return { type: 'percentage', icon: <Percent className="h-3 w-3" /> }
  }
  if (keyLower.includes('traffic') || keyLower.includes('position')) {
    return { type: 'number', icon: <TrendingUp className="h-3 w-3" /> }
  }
  if (!isNaN(Number(value)) && Number(value) > 0) {
    return { type: 'number', icon: <Hash className="h-3 w-3" /> }
  }

  return { type: 'text' }
}

const getTypeIcon = (type: string): JSX.Element => {
  switch (type) {
    case 'difficulty':
      return <BarChart3 className="h-3 w-3" />
    case 'volume':
      return <Search className="h-3 w-3" />
    case 'currency':
      return <DollarSign className="h-3 w-3" />
    case 'percentage':
      return <Percent className="h-3 w-3" />
    case 'number':
      return <Hash className="h-3 w-3" />
    case 'text':
      return <Eye className="h-3 w-3" />
    default:
      return <Eye className="h-3 w-3" />
  }
}

// Get color based on difficulty value
const getDifficultyColor = (value: string | number): string => {
  const num = typeof value === 'string' ? parseInt(value) : value
  if (num >= 80) return 'text-red-600 bg-red-50'
  if (num >= 60) return 'text-orange-600 bg-orange-50'
  if (num >= 40) return 'text-yellow-600 bg-yellow-50'
  return 'text-green-600 bg-green-50'
}

// Get color based on volume (higher is better)
const getVolumeColor = (value: string | number): string => {
  const num = typeof value === 'string' ? parseInt(value.replace(/[,\s]/g, '')) : value
  if (num >= 50000) return 'text-green-600 bg-green-50'
  if (num >= 10000) return 'text-blue-600 bg-blue-50'
  if (num >= 1000) return 'text-yellow-600 bg-yellow-50'
  return 'text-gray-600 bg-gray-50'
}

// Calculate optimal column widths
const calculateColumnWidths = (data: Record<string, any>[], allKeys: string[]) => {
  const minWidth = 100
  const maxWidth = 300
  const padding = 32

  const widths: Record<string, number> = {}

  for (const key of allKeys) {
    let maxContentWidth = key.length * 8

    const sampleSize = Math.min(10, data.length)
    for (let i = 0; i < sampleSize; i++) {
      const value = data[i][key]
      const displayValue =
        value === null || value === undefined
          ? ''
          : typeof value === 'object'
            ? JSON.stringify(value)
            : String(value)

      const contentWidth = displayValue.length * 7
      maxContentWidth = Math.max(maxContentWidth, contentWidth)
    }

    widths[key] = Math.min(maxWidth, Math.max(minWidth, maxContentWidth + padding))
  }

  return widths
}

// Enhanced table cell component
const EnhancedTableCell = memo(
  ({
    value,
    columnType,
    isHeader = false,
    className = '',
  }: {
    value: any
    columnType: ReturnType<typeof getColumnType>
    isHeader?: boolean
    className?: string
  }) => {
    const displayValue = value === null || value === undefined ? '-' : String(value)

    if (isHeader) {
      return (
        <div className={`flex items-center gap-2 font-semibold text-gray-700 ${className}`}>
          {columnType.icon}
          <span className="truncate">{displayValue}</span>
        </div>
      )
    }

    // Apply styling based on column type
    const cellContent = displayValue
    let cellClassName = 'truncate'

    switch (columnType.type) {
      case 'difficulty':
        const difficultyColors = getDifficultyColor(displayValue)
        cellClassName = `truncate px-2 py-1 rounded-md text-xs font-medium ${difficultyColors}`
        break
      case 'volume':
        const volumeColors = getVolumeColor(displayValue)
        cellClassName = `truncate px-2 py-1 rounded-md text-xs font-medium ${volumeColors}`
        break
      case 'currency':
        cellClassName = 'truncate font-mono text-green-600 font-medium'
        break
      case 'percentage':
        cellClassName = 'truncate font-medium text-blue-600'
        break
      case 'number':
        cellClassName = 'truncate font-mono text-gray-700'
        break
      default:
        cellClassName = 'truncate text-gray-900'
    }

    return (
      <div className={`${cellClassName} ${className}`}>
        {columnType.type === 'percentage' && !displayValue.includes('%') && displayValue !== '-'
          ? `${displayValue}%`
          : cellContent}
      </div>
    )
  }
)

EnhancedTableCell.displayName = 'EnhancedTableCell'

// Enhanced table row component
const EnhancedTableRow = memo(
  ({
    processedRow,
    allKeys,
    priorityColumns,
    columnWidths,
    colorCodedColumns,
    isEven,
  }: {
    processedRow: Record<string, string>
    allKeys: string[]
    priorityColumns?: string[]
    columnWidths: Record<string, number>
    colorCodedColumns?: Record<string, string>
    isEven: boolean
  }) => {
    const [isHovered, setIsHovered] = useState(false)

    return (
      <tr
        className={`transition-all duration-150 ${
          isEven ? 'bg-gray-25' : 'bg-white'
        } hover:bg-blue-50 hover:shadow-sm ${isHovered ? 'scale-[1.01]' : ''}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {allKeys.map((key, colIndex) => {
          const value = processedRow[key]
          const isPriority = !priorityColumns || priorityColumns.includes(key)
          const hiddenClass = isPriority ? '' : 'hidden sm:table-cell'
          const columnType = getColumnType(key, value, colorCodedColumns)

          return (
            <td
              key={colIndex}
              className={`border-b border-gray-100 px-4 py-3 text-sm ${hiddenClass}`}
              style={{ width: columnWidths[key] }}
            >
              <EnhancedTableCell value={value} columnType={columnType} />
            </td>
          )
        })}
      </tr>
    )
  }
)

EnhancedTableRow.displayName = 'EnhancedTableRow'

export function EnhancedDataTable({
  data,
  maxRows = 100,
  title,
  priorityColumns,
  colorCodedColumns,
}: Props) {
  const [sortKey, setSortKey] = useState<string | null>(null)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')

  // Memoize expensive calculations and pre-process data
  const { processedData, hasMoreRows, allKeys, desktopOnlyKeys, columnWidths, totalWidth } =
    useMemo(() => {
      if (!data || data.length === 0) {
        return {
          processedData: [],
          hasMoreRows: false,
          allKeys: [],
          desktopOnlyKeys: [],
          columnWidths: {},
          totalWidth: 0,
        }
      }

      const sortedData = [...data]

      // Apply sorting if requested
      if (sortKey) {
        sortedData.sort((a, b) => {
          const aVal = a[sortKey]
          const bVal = b[sortKey]

          // Handle numeric sorting
          const aNum = Number(String(aVal).replace(/[,\s$%]/g, ''))
          const bNum = Number(String(bVal).replace(/[,\s$%]/g, ''))

          if (!isNaN(aNum) && !isNaN(bNum)) {
            return sortDirection === 'asc' ? aNum - bNum : bNum - aNum
          }

          // String sorting
          const aStr = String(aVal).toLowerCase()
          const bStr = String(bVal).toLowerCase()

          if (sortDirection === 'asc') {
            return aStr.localeCompare(bStr)
          } else {
            return bStr.localeCompare(aStr)
          }
        })
      }

      const displayData = sortedData.slice(0, maxRows)
      const hasMoreRows = sortedData.length > maxRows

      // Extract all keys from sample data
      const sampleSize = Math.min(10, sortedData.length)
      const allKeys = [
        ...new Set(sortedData.slice(0, sampleSize).flatMap((obj) => Object.keys(obj))),
      ]

      // Calculate optimal column widths
      const columnWidths = calculateColumnWidths(displayData, allKeys)
      const totalWidth = Object.values(columnWidths).reduce((sum, width) => sum + width, 0)

      // Pre-process all display data once
      const processedData = displayData.map((row) => {
        const processedRow: Record<string, string> = {}
        for (const key of allKeys) {
          const value = row[key]
          processedRow[key] =
            value === null || value === undefined
              ? ''
              : typeof value === 'object'
                ? JSON.stringify(value)
                : String(value)
        }
        return processedRow
      })

      // Separate keys for responsive display
      const desktopOnlyKeys = priorityColumns
        ? allKeys.filter((key) => !priorityColumns.includes(key))
        : allKeys.slice(3)

      return { processedData, hasMoreRows, allKeys, desktopOnlyKeys, columnWidths, totalWidth }
    }, [data, maxRows, priorityColumns, sortKey, sortDirection])

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDirection('asc')
    }
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="mb-4 rounded-full bg-gray-100 p-3">
          <BarChart3 className="h-6 w-6 text-gray-400" />
        </div>
        <div className="text-gray-500">No data to display</div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {title && (
        <div className="flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
            <BarChart3 className="h-4 w-4 text-blue-600" />
            {title}
          </h3>
          {desktopOnlyKeys.length > 0 && (
            <div className="text-xs text-gray-500 sm:hidden">→ Scroll for more columns</div>
          )}
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg">
        <div
          className="max-h-[500px] overflow-auto"
          style={{
            WebkitOverflowScrolling: 'touch',
            touchAction: 'pan-x pan-y',
          }}
        >
          <table
            className="text-left text-sm"
            style={{
              width: Math.max(totalWidth, 600),
              tableLayout: 'fixed',
            }}
          >
            <thead className="sticky top-0 z-10 bg-gradient-to-r from-gray-50 to-gray-100 backdrop-blur-sm">
              <tr className="border-b-2 border-gray-200">
                {allKeys.map((key) => {
                  const isPriority = !priorityColumns || priorityColumns.includes(key)
                  const hiddenClass = isPriority ? '' : 'hidden sm:table-cell'
                  const columnType = getColumnType(key, '', colorCodedColumns)
                  const isSorted = sortKey === key

                  return (
                    <th
                      key={key}
                      className={`cursor-pointer select-none whitespace-nowrap px-4 py-3 text-left transition-colors hover:bg-gray-100 ${hiddenClass}`}
                      style={{ width: columnWidths[key] }}
                      onClick={() => handleSort(key)}
                    >
                      <div className="flex items-center justify-between">
                        <EnhancedTableCell value={key} columnType={columnType} isHeader={true} />
                        {isSorted && (
                          <div className="ml-2 flex-shrink-0">
                            {sortDirection === 'asc' ? (
                              <ArrowUp className="h-3 w-3 text-blue-600" />
                            ) : (
                              <ArrowDown className="h-3 w-3 text-blue-600" />
                            )}
                          </div>
                        )}
                      </div>
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {processedData.map((processedRow, rowIndex) => (
                <EnhancedTableRow
                  key={rowIndex}
                  processedRow={processedRow}
                  allKeys={allKeys}
                  priorityColumns={priorityColumns}
                  columnWidths={columnWidths}
                  colorCodedColumns={colorCodedColumns}
                  isEven={rowIndex % 2 === 0}
                />
              ))}
            </tbody>
          </table>
        </div>
        {hasMoreRows && (
          <div className="border-t border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3">
            <div className="flex items-center justify-between text-xs text-gray-700">
              <span>
                Showing {maxRows} of {data.length} rows
                {desktopOnlyKeys.length > 0 && (
                  <span className="ml-2 sm:hidden">• Limited columns on mobile</span>
                )}
              </span>
              <div className="flex items-center gap-1 text-blue-600">
                <TrendingUp className="h-3 w-3" />
                <span>Click headers to sort</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
